import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Card, Button, QuantitySelector } from '../components/ui';
import { useState, useEffect } from 'react';
import { trackCartEvent } from '../services/analytics';

const Cart = () => {
  const {
    state: { items },
    dispatch,
  } = useCart();
  const navigate = useNavigate();
  const [viewportHeight, setViewportHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      const vh = window.visualViewport?.height || window.innerHeight;
      setViewportHeight(vh);
    };
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.visualViewport?.addEventListener('resize', updateHeight);
    
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.visualViewport?.removeEventListener('resize', updateHeight);
    };
  }, []);

  const total = items.reduce((sum, i) => sum + i.product.price * i.qty, 0);

  const updateQty = (id, qty) => {
    trackCartEvent({
      action: 'quantity_update',
      status: 'started',
      productId: id,
      metadata: { new_quantity: qty }
    });
    
    try {
      dispatch({ type: 'UPDATE_QTY', payload: { id, qty } });
      trackCartEvent({
        action: 'quantity_update',
        status: 'succeeded',
        productId: id,
        metadata: { new_quantity: qty }
      });
    } catch (error) {
      trackCartEvent({
        action: 'quantity_update',
        status: 'failed',
        productId: id,
        errorMessage: error.message,
        metadata: { new_quantity: qty }
      });
    }
  };

  const remove = (id) => {
    trackCartEvent({
      action: 'remove_from_cart',
      status: 'started',
      productId: id
    });
    
    try {
      dispatch({ type: 'REMOVE', payload: id });
      trackCartEvent({
        action: 'remove_from_cart',
        status: 'succeeded',
        productId: id
      });
    } catch (error) {
      trackCartEvent({
        action: 'remove_from_cart',
        status: 'failed',
        productId: id,
        errorMessage: error.message
      });
    }
  };

  // Calculate 75% of viewport height minus header
  const headerHeight = 80;
  const contentHeight = viewportHeight > 0 ? Math.floor(viewportHeight * 0.75) : 600;
  const availableHeight = Math.max(contentHeight - headerHeight, 400);

  return (
    <main className="bg-light-secondary dark:bg-brand-dark min-h-screen transition-colors duration-200">
      {/* Centered Container with max-width: 1200px */}
      <div className="max-w-[1200px] mx-auto px-[16px] md:px-[24px] py-[16px] md:py-[32px]">
        <h1 className="text-[24px] md:text-[28px] font-bold mb-[16px] md:mb-[32px] text-light-text-primary dark:text-brand-text-primary">
          Your Cart
        </h1>
        
        {items.length === 0 ? (
          <div className="text-center py-[60px]">
            <p className="text-[16px] text-light-text-secondary dark:text-brand-text-secondary">
              Cart is empty. <Link to="/catalog" className="text-brand-accent-red font-medium hover:underline">Shop now</Link>
            </p>
          </div>
        ) : (
          /* Two-Column Grid: 2/3 (Cart Items) vs 1/3 (Order Summary) */
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-[32px] items-start">
            {/* Left Column: Cart Items */}
            <div className="w-full">
              <div className="space-y-[16px]">
                {items.map(({ product, qty }) => {
                  const availableRaw = product.inventory ?? product.stock;
                  const available = Number.isFinite(Number(availableRaw)) ? Math.max(0, Number(availableRaw)) : undefined;
                  const maxAttr = Number.isFinite(available) ? available : undefined;

                  return (
                    /* Cart Item Card - Proper Structure */
                    <div 
                      key={product._id} 
                      className="relative bg-white dark:bg-brand-surface p-[20px] rounded-[18px] border border-[#E5E5E5] dark:border-brand-border-default shadow-[0px_2px_8px_rgba(0,0,0,0.04)] transition-colors"
                    >
                      {/* Remove Button - Top Right Corner with Minus Symbol */}
                      <button 
                        onClick={() => remove(product._id)}
                        className="absolute top-[12px] right-[12px] w-[32px] h-[32px] flex items-center justify-center rounded-full bg-light-secondary dark:bg-brand-elevated text-light-text-primary dark:text-brand-text-primary hover:bg-red-50 hover:text-brand-accent-red dark:hover:bg-red-900/20 dark:hover:text-brand-accent-red transition-colors border border-light-border-default dark:border-brand-border-default"
                        aria-label="Remove item from cart"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                      </button>

                      <div className="flex gap-[20px]">
                        {/* Product Image: 96x96 */}
                        <div className="w-[96px] h-[96px] flex-shrink-0 bg-light-secondary dark:bg-brand-surface rounded-[12px] overflow-hidden">
                          <picture>
                            <source 
                              srcSet={
                                product.imageUrl?.includes('res.cloudinary.com') 
                                  ? product.imageUrl.replace('/upload/', '/upload/f_webp/') 
                                  : product.imageUrl?.replace(/\.(jpg|jpeg|png)$/i, '.webp')
                              } 
                              type="image/webp" 
                            />
                            <img 
                              src={product.imageUrl} 
                              alt={product.name} 
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover" 
                            />
                          </picture>
                        </div>
                        
                        {/* Product Details Column */}
                        <div className="flex-grow flex flex-col gap-[16px] pr-[32px]">
                          {/* Product Title */}
                          <Link to={`/product/${product._id}`} className="hover:underline">
                            <h3 className="font-semibold text-[16px] leading-[1.4] text-light-text-primary dark:text-brand-text-primary">
                              {product.name}
                            </h3>
                          </Link>
                          
                          {/* Product Price - Single Line */}
                          <p className="font-bold text-[18px] text-light-text-primary dark:text-brand-text-primary whitespace-nowrap">
                            UGX {product.price.toLocaleString()}
                          </p>
                          
                          {/* Quantity Selector */}
                          <div className="flex-shrink-0 mt-[4px]">
                            <QuantitySelector 
                              quantity={qty}
                              onQuantityChange={(newQty) => updateQty(product._id, newQty)}
                              max={maxAttr}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column: Order Summary - Aligned with First Cart Item */}
            <div className="w-full lg:sticky lg:top-[100px]">
              <Card 
                variant="elevated" 
                className="p-[24px] rounded-[18px] bg-white dark:bg-brand-surface border border-[#E5E5E5] dark:border-brand-border-default shadow-[0px_4px_16px_rgba(0,0,0,0.08)] transition-colors"
              >
                <h2 className="text-[20px] font-bold text-light-text-primary dark:text-brand-text-primary mb-[24px]">
                  Order Summary
                </h2>
                
                {/* Cost Breakdown with Proper Spacing */}
                <div className="space-y-[16px] mb-[24px]">
                  {/* Subtotal Row */}
                  <div className="flex justify-between items-center">
                    <span className="text-[15px] text-light-text-secondary dark:text-brand-text-secondary">
                      Subtotal
                    </span>
                    <span className="text-[15px] font-semibold text-light-text-primary dark:text-brand-text-primary">
                      UGX {total.toLocaleString()}
                    </span>
                  </div>
                  
                  {/* Shipping Row: Clear Label-Value Pair */}
                  <div className="flex justify-between items-center">
                    <span className="text-[15px] text-light-text-secondary dark:text-brand-text-secondary">
                      Shipping
                    </span>
                    <span className="text-[14px] font-normal text-light-text-secondary dark:text-brand-text-secondary">
                      Calculated at checkout
                    </span>
                  </div>
                </div>
                
                {/* Total Section with Border Separator */}
                <div className="border-t border-[#E5E5E5] dark:border-brand-border-default pt-[20px] mb-[24px]">
                  <div className="flex justify-between items-baseline">
                    <span className="text-[18px] font-bold text-light-text-primary dark:text-brand-text-primary">
                      Total
                    </span>
                    <span className="text-[24px] font-bold text-light-text-primary dark:text-brand-text-primary">
                      UGX {total.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Checkout Button: Full Width */}
                <Button
                  className="w-full h-[56px] text-[16px] rounded-[12px] bg-black text-white font-semibold hover:bg-black/90 active:scale-[0.98] transition-all flex items-center justify-center"
                  onClick={() => navigate('/delivery')}
                >
                  Checkout
                </Button>
              </Card>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default Cart;
